import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/composites/PageHeader';
import { SectionCard } from '@/components/composites/SectionCard';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { Button } from '@/components/primitives/Button';
import { TextInput } from '@/components/primitives/TextInput';
import { Select } from '@/components/primitives/Select';
import { Toggle } from '@/components/primitives/Toggle';
import { ActionLink } from '@/components/primitives/ActionLink';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useBusinessUnits } from '@/hooks/useBusinessUnits';
import { useAppContext } from '@/context/AppContext';
import { apiFetch } from '@/lib/api';
import { noop } from '@/lib/noop';
import type { OrgSettings } from '@/data/orgSettings';
import {
  ORG_KILL_SWITCH_HINT,
  ORG_KILL_SWITCH_TOGGLE_LABEL,
  KILL_SWITCH_MODAL_TITLE,
  KILL_SWITCH_MODAL_BODY,
  KILL_SWITCH_MODAL_CONFIRM_BUTTON,
  KILL_SWITCH_MODAL_CANCEL_BUTTON,
} from '@/content/activity-copy';
import styles from './SettingsOrgPage.module.css';

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
] as const;

// Outer gate. The inner form is only rendered once real server data is
// available for the *current* org, and is keyed on `org.id` so switching
// Clerk org remounts the form with fresh useState — no fallback flashes
// from the fixture or the previous org's values.
export function SettingsOrgPage() {
  const { data: org, isLoading } = useOrgSettings();

  return (
    <>
      <PageHeader title="Organization" />
      {org == null ? (
        <SectionCard title="General">
          <p className={styles.loading}>{isLoading ? 'Loading…' : 'Organization unavailable.'}</p>
        </SectionCard>
      ) : (
        <SettingsOrgForm key={org.id} org={org} />
      )}
    </>
  );
}

function SettingsOrgForm({ org }: { org: OrgSettings }) {
  const { data: businessUnits } = useBusinessUnits();
  const { currentOrg, setCurrentOrg, getToken, mockupMode, reloadOrg } = useAppContext();
  const queryClient = useQueryClient();

  // General section — initialized from the server response, not a fixture.
  const [orgName, setOrgName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [timezone, setTimezone] = useState(org.timezone);
  const [generalSaving, setGeneralSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Data Configuration section
  const [mps, setMps] = useState(org.maxMinutesPerScreenshot);
  const [fullDay, setFullDay] = useState(org.fullDayThresholdHours);
  const [halfDay, setHalfDay] = useState(org.halfDayThresholdHours);
  const [halfWeight, setHalfWeight] = useState(org.halfDayWeight);
  const [dataSaving, setDataSaving] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // Invitations section — 104e D2. The toggle is a single-field form; we
  // PATCH the one boolean on change (no Save button) so the surface is
  // closer to the Membership-role UX on the Access page and cannot drift
  // out of sync with the server.
  const [autoInvite, setAutoInvite] = useState(org.autoInviteOnEmployeeDiscovery);
  const [autoInviteSaving, setAutoInviteSaving] = useState(false);
  const [autoInviteError, setAutoInviteError] = useState<string | null>(null);

  // Privacy & AI controls — Feature 200. Org-level kill switch for the
  // Activity AI pipeline. ON→OFF transition shows a confirmation modal.
  const [activityAiEnabled, setActivityAiEnabled] = useState(org.activityAiPipelineEnabled);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [killSwitchModalOpen, setKillSwitchModalOpen] = useState(false);

  // Task 110 D4 — opt-in email-synthesis domain. The form initializes
  // from the server row (null → empty string for the input) and persists
  // on Save. Empty string round-trips to null on the wire (admin opting
  // out). Client-side validation mirrors the server-side regex
  // /^[a-z0-9.-]+\.[a-z]{2,}$/ — rejects `@`, spaces, uppercase, leading
  // dots — so the admin sees a friendly error before the request fires.
  const [emailDomain, setEmailDomain] = useState(org.insightfulEmailDomain ?? '');
  const [emailDomainSaving, setEmailDomainSaving] = useState(false);
  const [emailDomainError, setEmailDomainError] = useState<string | null>(null);

  async function handleSaveGeneral(e: FormEvent) {
    e.preventDefault();
    if (mockupMode) return;
    setGeneralSaving(true);
    setGeneralError(null);
    try {
      const token = await getToken();
      await apiFetch(`/v1/orgs/${currentOrg.id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ name: orgName, slug, timezone }),
      });
      // Invalidate with the same composite key the query uses so the form
      // refetches and shows the saved values.
      await queryClient.invalidateQueries({ queryKey: ['orgSettings', currentOrg.id] });
      // Keep the locally cached org label in step with the save so sidebar
      // and topbar updates don't wait for a Clerk round-trip.
      setCurrentOrg({ ...currentOrg, name: orgName, slug });
      // The backend syncs name/slug into Clerk, but Clerk's cached
      // organization record in the browser is stale until told to reload.
      // This is what keeps the Clerk OrganizationSwitcher from showing the
      // old name after a rename.
      try {
        await reloadOrg();
      } catch {
        // Non-fatal — the backend has already persisted the change, and
        // a full page reload will pick up the new name anyway.
      }
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setGeneralSaving(false);
    }
  }

  async function handleSaveDataConfig(e: FormEvent) {
    e.preventDefault();
    if (mockupMode) return;
    setDataError(null);

    // Clearing a <input type="number"> yields Number('') === NaN, which Zod
    // rejects as a 400 at the backend. Catch that locally so the user sees
    // a specific message instead of "Bad Request".
    const errors: string[] = [];
    if (!Number.isFinite(mps) || mps < 1 || mps > 120) {
      errors.push('Max Minutes per Insightful Event must be a number between 1 and 120.');
    }
    if (!Number.isFinite(fullDay) || fullDay < 0 || fullDay > 24) {
      errors.push('Full Day Threshold must be between 0 and 24 hours.');
    }
    if (!Number.isFinite(halfDay) || halfDay < 0 || halfDay > 24) {
      errors.push('Half Day Threshold must be between 0 and 24 hours.');
    }
    if (!Number.isFinite(halfWeight) || halfWeight < 0 || halfWeight > 1) {
      errors.push('Half Day Weight must be between 0 and 1.');
    }
    if (errors.length > 0) {
      setDataError(errors.join(' '));
      return;
    }

    setDataSaving(true);
    try {
      const token = await getToken();
      await apiFetch(`/v1/orgs/${currentOrg.id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({
          maxMinutesPerScreenshot: mps,
          fullDayThresholdHours: fullDay,
          halfDayThresholdHours: halfDay,
          halfDayWeight: halfWeight,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ['orgSettings', currentOrg.id] });
    } catch (err) {
      setDataError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setDataSaving(false);
    }
  }

  async function handleSaveEmailDomain(e: FormEvent) {
    e.preventDefault();
    setEmailDomainError(null);

    const trimmed = emailDomain.trim();
    // Mirror of the server-side validation in
    // web/src/routes/orgs.ts (`INSIGHTFUL_EMAIL_DOMAIN_RE`). Empty string
    // is valid here — it round-trips to null on the wire (clear setting).
    // Validation runs BEFORE the mockup short-circuit so the smoke test
    // (and any future mockup-mode operator) can verify the message.
    if (trimmed !== '' && !/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/.test(trimmed)) {
      setEmailDomainError(
        'Enter a lowercase domain like "example.com" — no leading "@", no spaces, no uppercase.',
      );
      return;
    }

    if (mockupMode) return;

    setEmailDomainSaving(true);
    try {
      const token = await getToken();
      await apiFetch(`/v1/orgs/${currentOrg.id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({
          insightfulEmailDomain: trimmed === '' ? null : trimmed,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ['orgSettings', currentOrg.id] });
    } catch (err) {
      setEmailDomainError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setEmailDomainSaving(false);
    }
  }

  async function handleToggleAutoInvite(next: boolean) {
    const previous = autoInvite;
    setAutoInvite(next);
    if (mockupMode) return;
    setAutoInviteSaving(true);
    setAutoInviteError(null);
    try {
      const token = await getToken();
      await apiFetch(`/v1/orgs/${currentOrg.id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ autoInviteOnEmployeeDiscovery: next }),
      });
      await queryClient.invalidateQueries({ queryKey: ['orgSettings', currentOrg.id] });
    } catch (err) {
      // Roll the local state back so the switch reflects reality.
      setAutoInvite(previous);
      setAutoInviteError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setAutoInviteSaving(false);
    }
  }

  async function handleToggleActivityAi(next: boolean) {
    // ON→OFF: show confirmation modal first.
    if (!next) {
      setKillSwitchModalOpen(true);
      return;
    }
    await applyActivityAiToggle(next);
  }

  async function applyActivityAiToggle(next: boolean) {
    const previous = activityAiEnabled;
    setActivityAiEnabled(next);
    if (mockupMode) return;
    setAiSaving(true);
    setAiError(null);
    try {
      const token = await getToken();
      await apiFetch(`/api/v1/orgs/${currentOrg.id}/settings`, token, {
        method: 'PATCH',
        body: JSON.stringify({ activity_ai_pipeline_enabled: next }),
      });
      await queryClient.invalidateQueries({ queryKey: ['orgSettings', currentOrg.id] });
    } catch (err) {
      setActivityAiEnabled(previous);
      setAiError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setAiSaving(false);
    }
  }

  // Business Units add/remove
  const [newBUName, setNewBUName] = useState('');
  const [buSaving, setBuSaving] = useState(false);
  const [buError, setBuError] = useState<string | null>(null);

  async function handleAddBU(e: FormEvent) {
    e.preventDefault();
    if (mockupMode) return;
    const name = newBUName.trim();
    if (!name) return;
    setBuSaving(true);
    setBuError(null);
    try {
      const token = await getToken();
      await apiFetch(`/v1/orgs/${currentOrg.id}/business-units`, token, {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      setNewBUName('');
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', currentOrg.id] });
    } catch (err) {
      setBuError(err instanceof Error ? err.message : 'Failed to add business unit');
    } finally {
      setBuSaving(false);
    }
  }

  async function handleRemoveBU(buId: string) {
    if (mockupMode) return;
    setBuError(null);
    try {
      const token = await getToken();
      await apiFetch(`/v1/orgs/${currentOrg.id}/business-units/${buId}`, token, {
        method: 'DELETE',
      });
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', currentOrg.id] });
    } catch (err) {
      setBuError(err instanceof Error ? err.message : 'Failed to remove business unit');
    }
  }

  // Delete org panel
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDeleteOrg() {
    if (deleteConfirm !== currentOrg.name) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const token = await getToken();
      await apiFetch(`/v1/orgs/${currentOrg.id}`, token, { method: 'DELETE' });
      // Reload — backend will return ORG_DELETED, bouncing to org selection gate
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete organization');
      setDeleting(false);
    }
  }

  return (
    <>
      {/* General */}
      <SectionCard title="General">
        <form onSubmit={handleSaveGeneral}>
          <div className={styles.fields}>
            <TextInput
              label="Organization Name"
              name="name"
              value={orgName}
              onChange={(e) => setOrgName(e.currentTarget.value)}
            />
            <TextInput
              label="Slug"
              name="slug"
              value={slug}
              onChange={(e) => setSlug(e.currentTarget.value)}
              className={styles.mono}
              hint="Used in URLs. Lowercase letters, numbers, and hyphens only."
            />
            <Select
              label="Timezone"
              value={timezone}
              options={TIMEZONE_OPTIONS}
              onChange={(v) => setTimezone(v)}
            />
          </div>
          {generalError && <p className={styles.formError}>{generalError}</p>}
          <div className={styles.sectionActions}>
            <Button variant="primary" type="submit" loading={generalSaving}>
              Save Changes
            </Button>
          </div>
        </form>
      </SectionCard>

      {/* Data Configuration */}
      <SectionCard title="Data Configuration">
        <form onSubmit={handleSaveDataConfig}>
          <div className={styles.dataGrid}>
            <TextInput
              label="Max Minutes per Insightful Event"
              name="maxMinutesPerScreenshot"
              type="number"
              value={String(mps)}
              onChange={(e) => setMps(Number(e.currentTarget.value))}
              hint="Cap on any single activity gap when calculating hours. Higher values count longer pauses as work time; lower values discount them. Recommended: 3.5."
            />
            <TextInput
              label="Full Day Threshold (hours)"
              name="fullDayThresholdHours"
              type="number"
              value={String(fullDay)}
              onChange={(e) => setFullDay(Number(e.currentTarget.value))}
            />
            <TextInput
              label="Half Day Threshold (hours)"
              name="halfDayThresholdHours"
              type="number"
              value={String(halfDay)}
              onChange={(e) => setHalfDay(Number(e.currentTarget.value))}
            />
            <TextInput
              label="Half Day Weight"
              name="halfDayWeight"
              type="number"
              value={String(halfWeight)}
              onChange={(e) => setHalfWeight(Number(e.currentTarget.value))}
            />
          </div>
          {dataError && <p className={styles.formError}>{dataError}</p>}
          <div className={styles.sectionActions}>
            <Button variant="primary" type="submit" loading={dataSaving}>
              Save Changes
            </Button>
          </div>
        </form>
      </SectionCard>

      {/* Invitations */}
      <SectionCard title="Invitations">
        <div className={styles.autoInviteRow}>
          <Toggle
            label="Automatically invite new employees to Kanjoy"
            checked={autoInvite}
            onChange={(v) => {
              void handleToggleAutoInvite(v);
            }}
            disabled={autoInviteSaving}
          />
        </div>
        <p className={styles.autoInviteHelp}>
          When a new employee is discovered during Insightful sync, send them a Kanjoy invitation
          email. You can always invite employees manually one at a time.
        </p>
        {autoInviteError && <p className={styles.formError}>{autoInviteError}</p>}

        {/* Task 110 D4 — opt-in email-synthesis domain */}
        <form onSubmit={handleSaveEmailDomain} className={styles.emailDomainForm}>
          <TextInput
            label="Insightful employee email domain"
            name="insightfulEmailDomain"
            value={emailDomain}
            placeholder="example.com"
            onChange={(e) => setEmailDomain(e.currentTarget.value)}
            hint={
              'When your Insightful tenant stores users by username (e.g. "alice"), Kanjoy appends ' +
              'this domain to build the email address for invite and auto-link. Leave blank to disable synthesis.'
            }
          />
          {emailDomainError && <p className={styles.formError}>{emailDomainError}</p>}
          <div className={styles.sectionActions}>
            <Button variant="primary" type="submit" loading={emailDomainSaving}>
              Save Changes
            </Button>
          </div>
        </form>
      </SectionCard>

      {/* Business Units */}
      <SectionCard title="Business Units">
        {businessUnits.length === 0 ? (
          <p className={styles.buEmpty}>No business units yet. Add one below.</p>
        ) : (
          <table className={styles.buTable}>
            <tbody>
              {businessUnits.map((unit, i) => {
                const isLast = i === businessUnits.length - 1;
                const rowStyle = isLast ? { borderBottom: 'none' } : undefined;
                return (
                  <tr key={unit.id}>
                    <td className={styles.buCell} style={rowStyle}>
                      {unit.name}
                    </td>
                    <td className={styles.buCellActions} style={rowStyle}>
                      <ActionLink
                        danger
                        onClick={(e) => {
                          e.preventDefault();
                          void handleRemoveBU(unit.id);
                        }}
                      >
                        Remove
                      </ActionLink>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <form onSubmit={handleAddBU} className={styles.buAddRow}>
          <div className={styles.buAddInput}>
            <TextInput
              label="New business unit"
              name="newBUName"
              value={newBUName}
              onChange={(e) => setNewBUName(e.currentTarget.value)}
              placeholder="e.g. Litigation"
            />
          </div>
          <Button variant="primary" type="submit" loading={buSaving} disabled={!newBUName.trim()}>
            Add
          </Button>
        </form>
        {buError && <p className={styles.formError}>{buError}</p>}
      </SectionCard>

      {/* Privacy & AI controls — Feature 200, admin-only */}
      <SectionCard title="Privacy &amp; AI controls">
        <div className={styles.autoInviteRow}>
          <Toggle
            label={ORG_KILL_SWITCH_TOGGLE_LABEL}
            checked={activityAiEnabled}
            onChange={(v) => {
              void handleToggleActivityAi(v);
            }}
            disabled={aiSaving}
          />
        </div>
        <p className={styles.autoInviteHelp}>{ORG_KILL_SWITCH_HINT}</p>
        {aiError && <p className={styles.formError}>{aiError}</p>}
      </SectionCard>

      {/* Kill-switch ON→OFF confirmation modal */}
      <SlidePanel
        open={killSwitchModalOpen}
        onClose={() => setKillSwitchModalOpen(false)}
        title={KILL_SWITCH_MODAL_TITLE}
        width="sm"
        footer={
          <div className={styles.deleteFooter}>
            <Button variant="secondary" onClick={() => setKillSwitchModalOpen(false)}>
              {KILL_SWITCH_MODAL_CANCEL_BUTTON}
            </Button>
            <Button
              variant="primary"
              loading={aiSaving}
              onClick={() => {
                setKillSwitchModalOpen(false);
                void applyActivityAiToggle(false);
              }}
            >
              {KILL_SWITCH_MODAL_CONFIRM_BUTTON}
            </Button>
          </div>
        }
      >
        <p className={styles.deleteWarning}>{KILL_SWITCH_MODAL_BODY}</p>
      </SlidePanel>

      {/* Danger Zone */}
      <div className={styles.dangerCard}>
        <h2 className={styles.dangerTitle}>Danger Zone</h2>
        <p className={styles.dangerText}>
          Mark this organization for deletion. Data will be retained but the organization will no
          longer be accessible.
        </p>
        <Button variant="danger" onClick={mockupMode ? noop : () => setDeleteOpen(true)}>
          Delete Organization
        </Button>
      </div>

      <SlidePanel
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteConfirm('');
          setDeleteError(null);
        }}
        title="Delete organization"
        width="sm"
        footer={
          <div className={styles.deleteFooter}>
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteConfirm('');
                setDeleteError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={deleteConfirm !== currentOrg.name}
              loading={deleting}
              onClick={handleDeleteOrg}
            >
              Delete Organization
            </Button>
          </div>
        }
      >
        <p className={styles.deleteWarning}>
          This will mark <strong>{currentOrg.name}</strong> for deletion. The organization and all
          its data will no longer be accessible.
        </p>
        <TextInput
          label={`Type "${currentOrg.name}" to confirm`}
          name="deleteConfirm"
          value={deleteConfirm}
          onChange={(e) => setDeleteConfirm(e.currentTarget.value)}
        />
        {deleteError && <p className={styles.deleteError}>{deleteError}</p>}
      </SlidePanel>
    </>
  );
}
